import { useCallback, useMemo } from 'react';
import { ApiError } from '../types/errors';
import { getErrorMessage } from '../config/errorMessages';

/**
 * Estrategia de notificación de errores.
 * Permite que cada módulo defina cómo quiere mostrar errores.
 */
export interface ErrorNotificationStrategy {
  /**
   * Muestra un error al usuario
   */
  showError: (message: string, error?: ApiError) => void;
  
  /**
   * Muestra una advertencia al usuario
   */
  showWarning?: (message: string) => void;
  
  /**
   * Muestra información al usuario
   */
  showInfo?: (message: string) => void;
}

/**
 * Opciones para personalizar el manejo de errores
 */
export interface ErrorHandlerOptions {
  /**
   * Estrategia de notificación (por defecto usa console)
   */
  notificationStrategy?: ErrorNotificationStrategy;
  
  /**
   * Mensajes personalizados por código de error
   */
  customMessages?: Record<string, string>;
  
  /**
   * Callback adicional cuando ocurre un error
   */
  onError?: (error: ApiError | Error) => void;
  
  /**
   * Si se debe hacer log del error en consola (por defecto: true en dev)
   */
  logToConsole?: boolean;
  
  /**
   * Mensaje genérico por defecto
   */
  defaultMessage?: string;
}

/**
 * Estrategia de notificación por defecto (console)
 */
const defaultNotificationStrategy: ErrorNotificationStrategy = {
  showError: (message: string, error?: ApiError) => {
    console.error('Error:', message, error);
  },
  showWarning: (_message: string) => {},
  showInfo: (_message: string) => {},
};

const EMPTY_CUSTOM_MESSAGES: Record<string, string> = {};
const DEFAULT_ERROR_MESSAGE = 'Ha ocurrido un error inesperado';
const IS_DEV = import.meta.env.DEV;

/**
 * Hook para manejo centralizado de errores de API.
 * 
 * Características:
 * - Normaliza errores de cualquier fuente
 * - Soporta múltiples estrategias de notificación
 * - Permite mensajes personalizados por código de error
 * - Type-safe con códigos de error del backend
 * 
 * @example
 * // Con Toast
 * const { handleError } = useApiError({
 *   notificationStrategy: {
 *     showError: showError, // de useToast()
 *   }
 * });
 * 
 * @example
 * // Con estado local
 * const [error, setError] = useState('');
 * const { handleError } = useApiError({
 *   notificationStrategy: {
 *     showError: (msg) => setError(msg),
 *   }
 * });
 * 
 * @example
 * // Con mensajes personalizados
 * const { handleError } = useApiError({
 *   customMessages: {
 *     'AUTH_INVALID_CREDENTIALS': 'Usuario o clave incorrectos',
 *   }
 * });
 */
export function useApiError(options: ErrorHandlerOptions = {}) {
  const {
    notificationStrategy = defaultNotificationStrategy,
    customMessages = EMPTY_CUSTOM_MESSAGES,
    onError,
    logToConsole = IS_DEV,
    defaultMessage = DEFAULT_ERROR_MESSAGE,
  } = options;

  // Stabilise object references so useCallback deps don't churn
  const stableStrategy = useMemo(
    () => notificationStrategy,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notificationStrategy.showError, notificationStrategy.showWarning, notificationStrategy.showInfo],
  );

  /**
   * Maneja un error de cualquier tipo y lo normaliza
   */
  const handleError = useCallback(
    (error: unknown): ApiError | Error => {
      let processedError: ApiError | Error;
      let message: string;

      // Normalizar el error
      if (error instanceof ApiError) {
        processedError = error;
        
        // Buscar mensaje personalizado, luego mensaje del sistema con interpolación, luego del backend
        message = 
          customMessages[error.code] || 
          getErrorMessage(error.code, error.message, error.details);

        // Log detallado para debugging
        if (logToConsole) {
          console.error('API Error:', {
            code: error.code,
            message: error.message,
            details: error.details,
            status: error.status,
            path: error.path,
            timestamp: error.timestamp,
          });
        }
      } else if (error instanceof Error) {
        processedError = error;
        message = error.message || defaultMessage;

        if (logToConsole) {
          console.error('Error:', error);
        }
      } else {
        // Error desconocido
        processedError = new Error(String(error));
        message = defaultMessage;

        if (logToConsole) {
          console.error('Unknown error:', error);
        }
      }

      // Notificar al usuario según severidad
      const isClientError = processedError instanceof ApiError 
        && processedError.status !== undefined 
        && processedError.status >= 400 
        && processedError.status < 500;

      if (isClientError && stableStrategy.showWarning) {
        stableStrategy.showWarning(message);
      } else {
        stableStrategy.showError(message, processedError instanceof ApiError ? processedError : undefined);
      }

      // Callback adicional si existe
      if (onError) {
        onError(processedError);
      }

      return processedError;
    },
    [customMessages, defaultMessage, logToConsole, stableStrategy, onError]
  );

  /**
   * Verifica si un error es un ApiError con un código específico
   */
  const isErrorCode = useCallback((error: unknown, code: string): boolean => {
    return error instanceof ApiError && error.is(code);
  }, []);

  /**
   * Verifica si un error es un ApiError con alguno de los códigos especificados
   */
  const isOneOfErrorCodes = useCallback((error: unknown, ...codes: string[]): boolean => {
    return error instanceof ApiError && error.isOneOf(...codes);
  }, []);

  return {
    handleError,
    isErrorCode,
    isOneOfErrorCodes,
  };
}
